/**
 * The list filter «script step not completed» (#879, PR 5) as the Records page
 * holds it: one string, `<stepId>:missed`, the very form the list API takes —
 * so the address, the saved set and the request are the same value.
 */
import {
  recordScriptStepQuerySchema,
  UNKNOWN_SCRIPT_STEP_ERROR_CODE,
  type RecordScriptStepFilter,
  type RecordScriptStepFilterEcho,
} from '@aica/api-types/schemas';
import { ApiError, isObject } from '@/lib/fetch-utils';

/** The filter a string names, or null when it is not one the server would accept. */
export function parseScriptStepFilter(raw: unknown): RecordScriptStepFilter | null {
  if (typeof raw !== 'string') return null;
  const parsed = recordScriptStepQuerySchema.safeParse(raw);
  return parsed.success && parsed.data ? parsed.data : null;
}

/** The address parameter as the page keeps it: the string when valid, nothing otherwise. */
export function scriptStepFilterParam(raw: unknown): string | undefined {
  return parseScriptStepFilter(raw) ? (raw as string) : undefined;
}

/**
 * The step's name from the list's answer — only when the answer is about THIS
 * step: the previous request's answer must not name the one just chosen.
 */
export function scriptStepEchoFor(
  filter: RecordScriptStepFilter | null,
  echo: RecordScriptStepFilterEcho | undefined | null
): RecordScriptStepFilterEcho | null {
  return filter && echo && echo.stepId === filter.stepId ? echo : null;
}

/**
 * The server refused the step of a `scriptStep` filter as unknown — the step
 * was removed from the script after the filter was saved or linked. Any other
 * refusal (another 400 included) is not this one. The list and the semantic
 * search answer it alike: both check the step before they read anything.
 */
export function isUnknownScriptStepRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    isObject(error.data) &&
    error.data.error === UNKNOWN_SCRIPT_STEP_ERROR_CODE
  );
}

/**
 * The semantic search refused the `scriptStep` KEY itself: its body is a strict
 * schema, so an edge-api older than the filter (rolled back behind the live
 * bundle) answers 400 «unrecognized key» to every search that carries it. The
 * list has its own sign of the same server — it answers without the echo; here
 * the refusal is all there is. The body is the validator's: the issues are a
 * JSON string in `error.message`. Any other validation refusal is not this one.
 */
export function isUnsupportedScriptStepRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 400 || !isObject(error.data)) {
    return false;
  }
  const failure = error.data.error;
  if (!isObject(failure) || typeof failure.message !== 'string') return false;
  let issues: unknown;
  try {
    issues = JSON.parse(failure.message);
  } catch {
    return false;
  }
  return (
    Array.isArray(issues) &&
    issues.some(
      (issue) =>
        isObject(issue) &&
        issue.code === 'unrecognized_keys' &&
        Array.isArray(issue.keys) &&
        issue.keys.includes('scriptStep')
    )
  );
}

/**
 * The step filter cannot stay on: the server refused its step as unknown or
 * does not know the filter at all. The page drops it on either.
 */
export function isScriptStepFilterRefusal(error: unknown): boolean {
  return isUnknownScriptStepRefusal(error) || isUnsupportedScriptStepRefusal(error);
}
